import { injectable } from "inversify";
import { GameMap, Scene } from "../../shared/domain";
import { GameStateDTO, TiledMapDTO } from "../../shared/dtos";
import { SpriteSheets } from "../../shared/enum";

export interface UIMapService {
    createTileMap(
        sceneDto: Scene,
        scene: Phaser.Scene,
        gameState: GameStateDTO,
        spawn?: any
    ): GameMap;

    playerSprite(): Phaser.Physics.Arcade.Sprite;
}

@injectable()
export class DefaultUIMapService implements UIMapService {
    private _playerSprite: Phaser.Physics.Arcade.Sprite;

    public playerSprite(): Phaser.Physics.Arcade.Sprite {
        return this._playerSprite;
    }

    public createTileMap(
        sceneDto: Scene,
        scene: Phaser.Scene,
        gameState: GameStateDTO,
        spawn?: any
    ): GameMap {
        const map = this._createTileMap(sceneDto, scene, gameState, spawn);
        map.createEnemySpawnPoints();
        return map;
    }

    public _createTileMap(
        sceneDto: Scene,
        scene: Phaser.Scene,
        gameState: GameStateDTO,
        spawn?: any
    ): GameMap {
        console.log("creating tilemap with spawn point: ", spawn);

        console.log("scene dto tiled map: ", sceneDto);

        // Guarded upstream by assertMapDataPresent, but createTileMap is also reached via the
        // already-cached branch of AbstractMapScene.create(), which skips initAssets.
        if (!sceneDto.tiledMap) {
            throw new Error(
                `Scene "${sceneDto.id}" has no map data, so no tilemap can be built. ` +
                    `Its map_id most likely points at a map that does not exist in its project.`
            );
        }

        // Validate map is not infinite - Phaser doesn't support infinite Tiled maps
        if (sceneDto.tiledMap.infinite) {
            throw new Error(
                `Map "${sceneDto.tiledMap.name}" is set to infinite mode. ` +
                    `Phaser does not support infinite Tiled maps. ` +
                    `Please disable infinite mode in Tiled Map Editor (Map → Map Properties → uncheck "Infinite").`
            );
        }

        const map = scene.make.tilemap({ key: sceneDto.id });

        const textureKeys = scene.textures.getTextureKeys();
        console.log(textureKeys);
        const collisionLayers = this.loadMap(sceneDto.tiledMap, map);

        scene.physics.world.bounds.width = map.widthInPixels;
        scene.physics.world.bounds.height = map.heightInPixels;

        // our player sprite created through the physics system
        const player = this.initializePlayer(map, scene, sceneDto, gameState, spawn);
        this._playerSprite = player;

        collisionLayers.forEach((layer) => {
            scene.physics.add.collider(player, layer);
        });

        return new GameMap(player, map, scene, sceneDto);
    }

    private initializePlayer(
        map: Phaser.Tilemaps.Tilemap,
        scene: Phaser.Scene,
        sceneDto: Scene,
        gameState: GameStateDTO,
        spawn?: any
    ): Phaser.Physics.Arcade.Sprite {
        // Use frame from sprite data, defaulting to 0 if not available
        let initialFrame: string | number = 0;
        if (gameState?.main_character_sprite_data) {
            initialFrame = gameState.main_character_sprite_data.frame_name ?? 0;
        }

        // Default spawn point from game state
        const defaultSpawnPoint = { x: gameState.game_location.x, y: gameState.game_location.y };

        // NEW: Find warp point by ID from Scene instead of searching Tiled map
        let spawnPoint = defaultSpawnPoint;
        if (spawn) {
            // spawn is now a warp point ID (UUID)
            const warpPoint = sceneDto.warpPoints?.find((warp) => warp.id === spawn);
            if (warpPoint) {
                spawnPoint = {
                    x: warpPoint.location.x + gameState.main_character_sprite_data.width / 2,
                    y: warpPoint.location.y,
                };
                console.log("Spawning at warp point:", warpPoint.name, spawnPoint);
            } else {
                console.warn(`Warp point with ID ${spawn} not found, using default spawn point`);
            }
        }

        const player = scene.physics.add.sprite(
            spawnPoint.x,
            spawnPoint.y,
            SpriteSheets.PLAYER,
            initialFrame
        );
        player.setScale(1, 1);
        player.setSize(16, 20);
        player.body.offset.y = 40;

        player.setCollideWorldBounds(true);

        return player;
    }

    private loadMap(
        mapData: TiledMapDTO,
        map: Phaser.Tilemaps.Tilemap
    ): Phaser.Tilemaps.TilemapLayer[] {
        const collisionLayers = [];
        const tilesetImages: Phaser.Tilemaps.Tileset[] = [];

        mapData.tilesets.forEach((tileset) => {
            //            let tilesetImage = map.addTilesetImage(tileset.name, 'tileset/'+tileset.image);
            const textureKey = "tileset/" + tileset.image;
            console.log("loading tileset with name: ", tileset.name, " and image: ", tileset.image);
            const tilesetImage = map.addTilesetImage(tileset.name, textureKey);
            if (!tilesetImage) {
                // Returns null when the texture was never loaded — otherwise this surfaces
                // much later as an unexplained failure inside createLayer.
                console.error(
                    `Tileset "${tileset.name}" could not be bound: no texture "${textureKey}". ` +
                        `The scene assets must contain a "tileset" asset named "${tileset.image}". ` +
                        `Available textures: ${map.scene.textures.getTextureKeys().join(", ")}`
                );
                return;
            }
            tilesetImages.push(tilesetImage);
        });
        mapData.layers
            .filter((layer) => layer.type === "tilelayer")
            .forEach((layer) => {
                const createdLayer = map.createLayer(layer.name, tilesetImages);
                if (
                    layer.properties &&
                    layer.properties.find((prop) => prop.name === "collide") &&
                    layer.properties.find((prop) => prop.name === "collide").value
                ) {
                    createdLayer.setCollisionByExclusion([-1]);
                    collisionLayers.push(createdLayer);
                }
            });

        return collisionLayers;
    }
}
